import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, Wand2, MessageSquare, Mail, Search } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function AIAssistant() {
  return (
    <div className="space-y-6" data-testid="ai-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Cosmic Intelligence</div>
        <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1 flex items-center gap-2">
          <Sparkles className="w-8 h-8 text-amber-500" strokeWidth={1.5} /> AI Assistant
        </h1>
        <p className="text-sm text-slate-500 mt-1">Powered by Claude Sonnet 4.5 — trained on Vedic principles</p>
      </div>

      <Tabs defaultValue="nlq">
        <TabsList className="rounded-full">
          <TabsTrigger value="nlq" className="rounded-full" data-testid="tab-nlq"><Search className="w-3.5 h-3.5 mr-1" /> Natural Language Search</TabsTrigger>
          <TabsTrigger value="whatsapp" className="rounded-full" data-testid="tab-whatsapp"><MessageSquare className="w-3.5 h-3.5 mr-1" /> WhatsApp Reply</TabsTrigger>
          <TabsTrigger value="email" className="rounded-full" data-testid="tab-email"><Mail className="w-3.5 h-3.5 mr-1" /> Email Generator</TabsTrigger>
          <TabsTrigger value="horoscope" className="rounded-full" data-testid="tab-horoscope"><Wand2 className="w-3.5 h-3.5 mr-1" /> Horoscope</TabsTrigger>
        </TabsList>

        <TabsContent value="nlq" className="mt-6"><NLQ /></TabsContent>
        <TabsContent value="whatsapp" className="mt-6"><WhatsApp /></TabsContent>
        <TabsContent value="email" className="mt-6"><EmailGen /></TabsContent>
        <TabsContent value="horoscope" className="mt-6"><Horoscope /></TabsContent>
      </Tabs>
    </div>
  );
}

function NLQ() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!q) return;
    setBusy(true);
    try { const { data } = await api.post("/ai/nlq", { query: q }); setRes(data); }
    catch (e) { console.error("NLQ error:", e); toast.error("AI unavailable"); }
    setBusy(false);
  };
  const examples = [
    "Show all business owners in Saturn Mahadasha",
    "Show clients who visited more than three times",
    "Show all pending Vastu consultations",
    "Show repeat clients",
  ];
  return (
    <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800">
      <Label className="text-xs uppercase tracking-widest text-slate-500">Ask in plain English</Label>
      <div className="flex gap-2 mt-2">
        <Input value={q} onChange={e=>setQ(e.target.value)} placeholder='e.g. "Show all business owners in Saturn Mahadasha"' className="h-12 rounded-xl" data-testid="nlq-input" />
        <Button onClick={run} disabled={busy} className="bg-[#1E3A8A] btn-pill h-12 px-6" data-testid="nlq-run">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Search</>}
        </Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {examples.map(ex => <button key={ex} onClick={()=>setQ(ex)} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 hover:border-amber-400">{ex}</button>)}
      </div>
      {res && (
        <div className="mt-6" data-testid="nlq-result">
          <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-sm">
            <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-400">Interpretation</div>
            <div className="mt-1">{res.interpretation}</div>
          </div>
          <div className="mt-4 text-sm">Matched: <span className="font-semibold">{res.results?.length}</span> {res.type}</div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
            {(res.results || []).slice(0, 12).map(r => (
              <Card key={r.id} className="p-3 rounded-xl text-sm">
                <div className="font-medium">{r.full_name || r.client_name || r.name}</div>
                <div className="text-xs text-slate-500">{r.mobile || ""} {r.mahadasha ? `• ${r.mahadasha}` : ""} {r.occupation ? `• ${r.occupation}` : ""}</div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function WhatsApp() {
  const [msg, setMsg] = useState(""); const [reply, setReply] = useState(""); const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai/whatsapp-reply", { message: msg }); setReply(data.reply); }
    catch (e) { console.error("whatsapp reply error:", e); toast.error("AI unavailable"); }
    setBusy(false);
  };
  return (
    <Card className="p-6 rounded-2xl">
      <Label className="text-xs uppercase tracking-widest text-slate-500">Client message</Label>
      <Textarea rows={4} value={msg} onChange={e=>setMsg(e.target.value)} placeholder="Hello sir, I want to know when my Sade Sati ends…" className="mt-2" />
      <Button onClick={run} className="mt-3 bg-[#1E3A8A] btn-pill" disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Draft reply</>}</Button>
      {reply && <div className="mt-4 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 whitespace-pre-wrap text-sm">{reply}</div>}
    </Card>
  );
}
function EmailGen() {
  const [purpose, setPurpose] = useState(""); const [points, setPoints] = useState(""); const [out, setOut] = useState(""); const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai/email", { purpose, points }); setOut(data.email); }
    catch (e) { console.error("email gen error:", e); toast.error("AI unavailable"); }
    setBusy(false);
  };
  return (
    <Card className="p-6 rounded-2xl grid grid-cols-1 md:grid-cols-2 gap-4">
      <div><Label className="text-xs uppercase tracking-widest text-slate-500">Purpose</Label><Input value={purpose} onChange={e=>setPurpose(e.target.value)} placeholder="Diwali greetings" className="mt-1" /></div>
      <div><Label className="text-xs uppercase tracking-widest text-slate-500">Key points</Label><Input value={points} onChange={e=>setPoints(e.target.value)} placeholder="10% discount on gemstones, muhurat pooja" className="mt-1" /></div>
      <div className="col-span-2"><Button onClick={run} className="bg-[#1E3A8A] btn-pill" disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate email"}</Button></div>
      {out && <div className="col-span-2 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 whitespace-pre-wrap text-sm">{out}</div>}
    </Card>
  );
}
function Horoscope() {
  const [form, setForm] = useState({ dob: "", tob: "", pob: "", rashi: "", nakshatra: "" });
  const [out, setOut] = useState(""); const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai/horoscope-summary", form); setOut(data.horoscope); }
    catch (e) { console.error("horoscope error:", e); toast.error("AI unavailable"); }
    setBusy(false);
  };
  return (
    <Card className="p-6 rounded-2xl grid grid-cols-1 md:grid-cols-2 gap-4">
      <div><Label className="text-xs uppercase tracking-widest text-slate-500">DOB</Label><Input type="date" value={form.dob} onChange={e=>setForm({...form,dob:e.target.value})} /></div>
      <div><Label className="text-xs uppercase tracking-widest text-slate-500">Time</Label><Input type="time" value={form.tob} onChange={e=>setForm({...form,tob:e.target.value})} /></div>
      <div><Label className="text-xs uppercase tracking-widest text-slate-500">Place</Label><Input value={form.pob} onChange={e=>setForm({...form,pob:e.target.value})} /></div>
      <div><Label className="text-xs uppercase tracking-widest text-slate-500">Rashi</Label><Input value={form.rashi} onChange={e=>setForm({...form,rashi:e.target.value})} /></div>
      <div className="col-span-2"><Button onClick={run} className="bg-[#1E3A8A] btn-pill" disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate yearly horoscope"}</Button></div>
      {out && <div className="col-span-2 p-4 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 whitespace-pre-wrap text-sm">{out}</div>}
    </Card>
  );
}
